// The Stats Table's group on ?live=1: the Ranking's (useLeagueGroups — the canvas player's league
// file by default, the League chip's leagues once ticked; the same chips), read for every column
// at once. Live only: the snapshot site hides the tile.

import { useMemo } from "react";
import type { HopsDistribution, ProtoMetricCategory, ProtoPlayerMetric, SquadResponse } from "@catbento/shared";
import type { PlayerId } from "../data/players.js";
import { getMetricCategories } from "../data/stats.js";
import { leagueOptionInfo } from "../modules/CountryFlag.js";
import { LIVE_RANKING_CHIPS, LIVE_RANKING_RANGE_FILTERS } from "../modules/rankings-view.js";
import { chipIsOn, type ActiveFilters, type CategoricalActive, type KitPlayer } from "../engines/stats-filter-kit.js";
import { distinctPlayers } from "../live/group-players.js";
import { chipsWithData, MINUTES_CHIP_DEFAULT } from "../live/stats-files.js";
import { liveStatsCategory } from "../live/stats.js";
import { tableRows, type HopsLookup, type StatsTableRow } from "../live/stats-table.js";
import { liveApi } from "../live/api.js";
import { useLiveQueries, useLiveQuery } from "../live/query.js";
import { computePercentile, parseDistribution } from "../modules/hops-curve.js";
import type { RankingChips } from "./useRankings.js";
import { chipOptionsOfGroups, filesOfGroups, groupsKey, playersOfGroups, refPlayerOf, useLiveLeagueGroups } from "./useLeagueGroups.js";

/** The stats a column can show: the Stats tiles' own categories (live), in their order. */
export function statsTableCategories(): ProtoMetricCategory[] {
  return getMetricCategories().map(liveStatsCategory);
}

/** Every stat by key (names, output types). */
export function statsTableCatalog(categories: readonly ProtoMetricCategory[]): Record<string, ProtoPlayerMetric> {
  const out: Record<string, ProtoPlayerMetric> = {};
  for (const c of categories) for (const s of c.subcategories) for (const m of s.playerMetrics) out[m.key] = m;
  return out;
}

export interface StatsTableBoardParams {
  /** The player the table opened for (`_statsTableSourcePlayer`): their league is the default. */
  sourcePlayer: PlayerId;
  leagueCleared: boolean;
  season: string | null;
  columns: readonly string[];
  useTotal: boolean;
  active: ActiveFilters;
  /** The Men's / Women's switch's pick (null = the player's gender). */
  womensPick: boolean | null;
}

export interface StatsTableBoard {
  seasonCodes: string[];
  season: string;
  rows: StatsTableRow[];
  /** Distinct players; null = nothing to show (`label` says why). */
  count: number | null;
  label: string;
  /** The leagues shown (the readout and a stat dragged out as a Ranking name them). */
  leagues: string[];
  chips: RankingChips;
  womens: boolean | null;
  /** The gender shown and the player's own (the Men's / Women's switch). */
  gender: { shown: boolean | null; player: boolean | null };
  defaultLeague: string | undefined;
  /** The canvas player's main position here (the default template). */
  refPosition: string | null;
}

// As the Ranking: a League chip added from "+ Add filter" starts with nothing ticked; Minutes at 150+.
const newChipValue = (key: string) => (key === "league" ? [] : key === "minutes" ? { ...MINUTES_CHIP_DEFAULT } : undefined);

export function useStatsTableBoard({ sourcePlayer, leagueCleared, season, columns, useTotal, active, womensPick }: StatsTableBoardParams): StatsTableBoard {
  const lg = useLiveLeagueGroups(sourcePlayer, season, active.league as CategoricalActive | undefined, leagueCleared, womensPick);
  const key = groupsKey(lg);
  // The Position chip's rule (rules.md): only minutes at each position, one row per position.
  const byPosition = chipIsOn(active.position);
  const refPlayer = useMemo<KitPlayer>(() => refPlayerOf(lg, sourcePlayer), [lg.home, sourcePlayer]);
  const options = useMemo(() => chipOptionsOfGroups(lg), [key, lg.leagueOptions]);
  // HOPS isn't in the Stats files: it's each player's own rating, served with their club's squad
  // (`/squad`, as the Squad module reads it). One call per club in the leagues shown, then the
  // gender's distribution places each rating as a percentile (as the Bio's).
  const clubs = lg.groups.flatMap(({ group }) =>
    Object.keys(group.file.teams).map((teamId) => ({ teamId, competitionId: group.file.competitionId, seasonId: group.file.seasonId })),
  );
  const squads = useLiveQueries<SquadResponse>(
    clubs.map((c) => ({
      key: `table:squad:${c.teamId}:${c.competitionId}:${c.seasonId}`,
      load: () => liveApi().get<SquadResponse>(`/squad?teamId=${c.teamId}&competitionId=${c.competitionId}&seasonId=${c.seasonId}`),
    })),
  );
  const womens = lg.womens;
  const distribution = useLiveQuery(womens === null ? null : `hops:distribution:${womens}`, () =>
    liveApi().get<HopsDistribution>(`/hops-distribution?isWomen=${womens}`),
  );
  const squadsKey = clubs.map((c) => `${c.teamId}@${c.seasonId}`).join(",");
  const hopsOf = useMemo<HopsLookup>(() => {
    const percentiles = parseDistribution(distribution)?.percentiles ?? [];
    const byId = new Map<number, { rating: number; percentile: number | null }>();
    for (const squad of squads) {
      for (const p of squad.players) {
        if (p.hops) byId.set(p.id, { rating: p.hops.rating, percentile: computePercentile(p.hops.rating, percentiles) });
      }
    }
    return (id) => byId.get(id) ?? null;
  }, [squadsKey, distribution]);

  const columnsKey = columns.join(",");
  const rows = useMemo(() => {
    if (lg.problem !== null) return [];
    const players = playersOfGroups(lg, byPosition ? "position" : "total", [...columns]);
    return tableRows(players, columns, lg.shown, useTotal, active, refPlayer, LIVE_RANKING_RANGE_FILTERS, hopsOf);
  }, [lg.problem, key, columnsKey, byPosition, useTotal, active, refPlayer, lg.shown, hopsOf]);

  const chips: RankingChips = {
    options,
    refPlayer,
    rangeFilters: LIVE_RANKING_RANGE_FILTERS,
    addMenuKeys: chipsWithData(LIVE_RANKING_CHIPS, filesOfGroups(lg)),
    newChipValue,
    optionInfo: { league: leagueOptionInfo(lg.leagues) },
  };
  const leagues = lg.groups.map((g) => g.label);
  const base = {
    seasonCodes: lg.seasonCodes,
    season: lg.shown,
    leagues,
    chips,
    defaultLeague: lg.defaultLeague,
    gender: { shown: lg.womens, player: lg.playerWomens },
    refPosition: (refPlayer.positionTemplate as string | null | undefined) ?? null,
  };
  if (lg.problem !== null) {
    const label = lg.cleared ? "Pick a league" : `Nothing to show: ${lg.problem}`;
    return { ...base, rows: [], count: null, label, womens: null };
  }
  return {
    ...base,
    rows,
    count: distinctPlayers(rows),
    label: lg.missingNote ? `players · ${lg.missingNote}` : "players",
    womens: lg.womens,
  };
}
